interface ProcessStep {
  step: string
  name: string
  description: string
  iconUrl: string
}

interface ProcessSectionProps {
  /** Title — first line in light blue, second in white */
  titleLine1: string
  titleLine2: string
  steps: ProcessStep[]
  note: string
}

function StepCard({ step, className = '' }: { step: ProcessStep; className?: string }) {
  return (
    <article className={`flex-1 w-full max-w-[280px] md:max-w-none lg:max-w-[208px] bg-white rounded-[24px] lg:rounded-[32px] shadow-[0px_4px_8px_rgba(0,0,0,0.5)] py-6 px-4 flex flex-col gap-6 lg:items-center ${className}`}>
      {/* Title row — desktop: stacked center / mobile: text left + icon right */}
      <div className="w-full flex items-center justify-between lg:flex-col lg:items-center lg:justify-center lg:gap-2">
        <div className="flex flex-col gap-2 items-start lg:items-center text-left lg:text-center">
          <p className="font-semibold text-qb-primary text-[18px] lg:text-[20px] leading-[1.5]">
            {step.step}
          </p>
          <h3 className="font-bold text-qb-text-high text-[24px] lg:text-[28px] leading-[1.4] tracking-[-0.6px]">
            {step.name}
          </h3>
        </div>
        <img
          src={step.iconUrl}
          alt=""
          aria-hidden
          className="w-[72px] h-[72px] lg:hidden object-contain"
        />
      </div>

      <p className="font-normal text-qb-text-mid text-[18px] leading-[1.5] tracking-[-0.6px] lg:text-center whitespace-normal lg:whitespace-pre-line">
        {step.description}
      </p>

      {/* Desktop-only icon — pinned to bottom so icons align across cards */}
      <img
        src={step.iconUrl}
        alt=""
        aria-hidden
        className="hidden lg:block lg:mt-auto w-20 h-20 object-contain"
      />
    </article>
  )
}

export function ProcessSection({
  titleLine1,
  titleLine2,
  steps,
  note,
}: ProcessSectionProps) {
  return (
    <section id="process" data-marketing-section="" className="w-full bg-[#16224f] pt-14 lg:pt-24 pb-32 px-6 tracking-[-0.6px]">
      <div className="mx-auto max-w-[1328px] flex flex-col items-center gap-14 lg:gap-20">
        {/* Title */}
        <div className="flex flex-col gap-4 items-center text-center font-bold text-[28px] lg:text-[40px] leading-[1.4] lg:leading-[1.2]">
          <p className="text-[#5ca8ff] whitespace-pre-line lg:whitespace-normal">
            {titleLine1}
          </p>
          <p className="text-white whitespace-pre-line lg:whitespace-normal">
            {titleLine2}
          </p>
        </div>

        {/* step cards */}
        <div className="w-full flex flex-col items-center gap-10 lg:gap-10">
          <div data-testid="process-step-grid" className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[208px_208px_208px] xl:flex xl:flex-row place-items-center md:place-items-stretch xl:items-stretch justify-center gap-4">
            {steps.map((step, i) => (
              <StepCard
                key={step.step}
                step={step}
                className={i === steps.length - 1 && steps.length % 2 === 1 ? 'md:col-span-2 lg:col-span-1' : ''}
              />
            ))}
          </div>

          <p className="text-white font-normal text-[18px] lg:text-[20px] leading-[1.5] text-center whitespace-pre-line">
            {note}
          </p>
        </div>
      </div>
    </section>
  )
}
